/** RN08: unmounted fleet view. RN-1 device ids, RN-14 owner controls, RN-18 privacy. */
import { useEffect, useRef, useState } from 'react';
import {
  ApiClientError, fetchRenderNodes, newRenderNodeRequestId, renderNodeFailureMessage,
  resetRenderNodeWatchdog, safeRenderNodeLabel, setRenderNodePower,
  setRenderNodeProcessingMode, wakeRenderNode,
} from '../../api/renderNodes';
import type { PowerAction, RenderNode, RenderNodeFleet, RenderNodeState } from '../../api/renderNodes';
import { listWorkspaces, type Workspace } from '../../api/workspaces';

type Control = PowerAction | 'wake' | 'mode' | 'reset';
const CONTROL_LABELS: Record<Control, string> = {
  wake: 'Wake', sleep: 'Sleep', restart: 'Restart', shutdown: 'Shut down', mode: 'Change processing mode', reset: 'Reset watchdog',
};
const STATE_LABELS: Record<RenderNodeState, string> = {
  online: 'Online', busy: 'Busy', draining: 'Draining', asleep_wakeable: 'Asleep · wake available',
  offline: 'Offline', quarantined: 'Quarantined',
};
const MODE_LABELS = { off: 'Off', headless_gpu_box: 'Headless GPU', laptop_light: 'Laptop light' };
const amount = (value: number | null, unit = '') => value === null ? 'Unknown' : `${value.toLocaleString()}${unit}`;

/** Disabled controls explain the same constraints checked again before dispatch. */
function unavailableReason(node: RenderNode, control: Control, entitled: boolean, pending: boolean, fresh: boolean,
  workspaceId: string): string {
  if (!entitled) return 'Render nodes are not included in your plan.';
  if (pending) return 'A node request is pending.';
  if (!fresh) return 'Refresh node status before using controls.';
  if (!workspaceId) return 'Select an active workspace for the Bridge control request.';
  if (node.state === 'busy' || node.state === 'draining' || (node.queue_depth ?? 0) > 0 || (node.in_flight ?? 0) > 0) {
    return 'This node is busy. Wait for queued and in-flight work to finish.';
  }
  if (control === 'wake') {
    if (!node.wake_via.length) return 'No awake waker is available for this node.';
    return node.state === 'asleep_wakeable' || node.state === 'offline' ? '' : 'This node is already awake.';
  }
  if (node.state === 'offline' || node.state === 'asleep_wakeable') return 'This node must be online.';
  if (node.queue_depth === null || node.in_flight === null) return 'Waiting for queue and in-flight status.';
  if (control === 'mode' && node.processing_mode === null) return 'Processing mode status is unknown.';
  if (control === 'reset' && !Object.values(node.watchdog).includes('quarantined')) {
    return 'No quarantined runtime needs a reset.';
  }
  return '';
}

export function RenderNodesPage() {
  const [fleet, setFleet] = useState<RenderNodeFleet | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [workspaceId, setWorkspaceId] = useState('');
  const [workspaceRead, setWorkspaceRead] = useState(false);
  const [entitled, setEntitled] = useState(true);
  const [fresh, setFresh] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [pending, setPending] = useState(false);
  const [ownerRefused, setOwnerRefused] = useState(false);
  const refresh = useRef<(() => Promise<void>) | null>(null);
  const busy = useRef(false);
  const epoch = useRef(0);
  const mounted = useRef(false);
  // Keep an uncertain request's id for an explicit retry of the same intent.
  const retry = useRef<{ deviceId: string; control: Control; mode: string | null; runtime: string | null; requestId: string } | null>(null);
  const isOwner = fleet?.viewer_role === 'owner' && !ownerRefused;

  useEffect(() => {
    let active = true;
    let reading: Promise<void> | null = null;
    mounted.current = true;
    const load = async (): Promise<void> => {
      if (reading) return reading;
      if (!active || document.visibilityState !== 'visible') return;
      const ticket = epoch.current;
      setFresh(false);
      const request = async () => {
        try {
          const [fleetResult, workspaceResult] = await Promise.allSettled([fetchRenderNodes(), listWorkspaces()]);
          if (!active || ticket !== epoch.current) return;
          if (fleetResult.status === 'rejected') throw fleetResult.reason;
          const result = fleetResult.value;
          setFleet(result);
          setEntitled(true);
          setLoadError('');
          setFresh(true);
          if (workspaceResult.status === 'fulfilled') {
            const activeWorkspaces = workspaceResult.value.filter((workspace) => workspace.state === 'active');
            setWorkspaces(activeWorkspaces);
            setWorkspaceId((current) => activeWorkspaces.some((workspace) => workspace.workspace_id === current)
              ? current : activeWorkspaces.length === 1 ? activeWorkspaces[0].workspace_id : '');
          } else {
            setWorkspaces([]);
            setWorkspaceId('');
          }
          setWorkspaceRead(true);
        } catch (failure) {
          if (!active || ticket !== epoch.current) return;
          // A revoked grant or membership must not leave its old nodes visible.
          setFleet(null);
          setLoadError(renderNodeFailureMessage(failure));
          if (failure instanceof ApiClientError && failure.code === 'render_nodes_not_entitled') setEntitled(false);
        }
      };
      reading = request();
      await reading;
      reading = null;
    };
    refresh.current = async () => {
      // Explicit refreshes wait for an older read, then share one new read.
      if (reading) await reading;
      await load();
    };
    void load();
    const poll = () => {
      if (!reading && !busy.current) void load();
    };
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') setFresh(false);
      else poll();
    };
    const timer = window.setInterval(poll, 10_000);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      active = false;
      mounted.current = false;
      refresh.current = null;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  async function controlNode(node: RenderNode, control: Control) {
    if (!isOwner || busy.current || unavailableReason(node, control, entitled, pending, fresh, workspaceId)) return;
    const label = safeRenderNodeLabel(node.label);
    if (!window.confirm(`${CONTROL_LABELS[control]} for “${label}”?${control === 'sleep' || control === 'restart' || control === 'shutdown'
      ? ' The node will drain work before the power action.' : ''}`)) return;
    busy.current = true;
    epoch.current += 1; // A pre-command poll cannot restore stale authority/status.
    setPending(true);
    setFresh(false);
    setError('');
    setNotice('');
    try {
      const prior = retry.current;
      const mode = control === 'mode' ? node.processing_mode : null;
      const runtime = control === 'reset'
        ? Object.entries(node.watchdog).find(([, status]) => status === 'quarantined')?.[0] ?? null : null;
      if (control === 'reset' && !runtime) return;
      const requestId = prior?.deviceId === node.device_id && prior.control === control
        && prior.mode === mode && prior.runtime === runtime
        ? prior.requestId : newRenderNodeRequestId();
      retry.current = { deviceId: node.device_id, control, mode, runtime, requestId };
      const request = { idempotencyKey: requestId };
      if (control === 'wake') await wakeRenderNode(node.device_id, workspaceId, request);
      else if (control === 'reset' && runtime) await resetRenderNodeWatchdog(node.device_id, workspaceId, runtime, request);
      else if (control === 'mode') await setRenderNodeProcessingMode(node.device_id, workspaceId, node.processing_mode === 'off' ? 'enter' : 'restore', request,
        node.display_class === 'headless_capable' ? 'headless_gpu_box' : 'laptop_light');
      else if (control === 'sleep' || control === 'restart' || control === 'shutdown') {
        await setRenderNodePower(node.device_id, workspaceId, control, request);
      }
      retry.current = null;
      if (mounted.current) setNotice('Request submitted. Waiting for updated node status.');
    } catch (failure) {
      if (!mounted.current) return;
      setError(renderNodeFailureMessage(failure));
      if (failure instanceof ApiClientError && failure.status === 403) {
        setOwnerRefused(true);
        setFleet(null);
        if (failure.code === 'render_nodes_not_entitled') setEntitled(false);
      }
      // Retain the id after an unknown outcome; retrying must not enqueue twice.
      if (failure instanceof ApiClientError && failure.kind === 'http' && failure.status < 500 && failure.status !== 408) retry.current = null;
    } finally {
      busy.current = false;
      if (mounted.current) {
        setPending(false);
        await refresh.current?.();
      }
    }
  }

  return (
    <section className="render-nodes-page" aria-label="Render nodes">
      <h1>Render Nodes</h1>
      <p>Monitor your connected render nodes and their available capacity.</p>
      {!entitled ? <p role="status">Render nodes are not included in your plan.</p> : null}
      {loadError && entitled ? <p className="error" role="alert">{loadError}</p> : null}
      {error && entitled ? <p className="error" role="alert">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}
      {!fleet && !loadError && entitled ? <p role="status">Loading render nodes…</p> : null}
      <button type="button" className="secondary" disabled={pending} onClick={() => void refresh.current?.()}>Refresh status</button>
      {fleet && entitled ? <>
        {!isOwner ? <p>Nodes shared with your account are read-only.</p> : null}
        {isOwner ? <>
          {workspaces.length > 0 ? <label htmlFor="render-node-workspace">Queue owner controls through workspace
            <select id="render-node-workspace" value={workspaceId} onChange={(event) => setWorkspaceId(event.target.value)}>
              <option value="">Select an active workspace</option>
              {workspaces.map((workspace) => <option key={workspace.workspace_id} value={workspace.workspace_id}>
                {workspace.ingress_host}
              </option>)}
            </select>
          </label> : workspaceRead ? <p role="status">No active workspace is available for queued node controls. Open an active workspace first.</p>
            : <p role="status">Loading active workspace choices…</p>}
        </> : null}
        {fleet.nodes.length === 0 ? <p>No render nodes are available. <a href="/docs/bridge/RENDER_NODE_SETUP.md">Set up a render node</a>.</p> : null}
        <div className="card-grid">
          {fleet.nodes.map((node) => (
            <article className="metric-card" key={node.device_id}>
              <h2>{safeRenderNodeLabel(node.label)} <span className="badge">{STATE_LABELS[node.state]}</span></h2>
              <p>VRAM free / total: {amount(node.vram_free_mib, ' MiB')} / {amount(node.vram_total_mib, ' MiB')}</p>
              {node.vram_total_mib !== null && node.vram_total_mib > 0 && node.vram_free_mib !== null ? (
                <meter min={0} max={node.vram_total_mib} value={node.vram_free_mib} aria-label={`Free VRAM for ${safeRenderNodeLabel(node.label)}`} style={{ width: '100%' }} />
              ) : <p>VRAM availability is unknown.</p>}
              <dl className="detail-list">
                <dt>Queue</dt><dd>{amount(node.queue_depth)}</dd>
                <dt>In-flight</dt><dd>{amount(node.in_flight)}</dd>
                <dt>Power limit</dt><dd>{amount(node.power.limit_w, ' W')}</dd>
                <dt>Power draw</dt><dd>{amount(node.power.draw_w, ' W')}</dd>
                <dt>Temperature</dt><dd>{amount(node.power.temp_c, ' °C')}</dd>
                <dt>Service mode</dt><dd>{node.service_mode ?? 'Unknown'}</dd>
                <dt>Processing mode</dt><dd>{node.processing_mode === null ? 'Unknown' : MODE_LABELS[node.processing_mode]}</dd>
              </dl>
              {node.power.cap_satisfied === false ? <p role="status">Power cap is not satisfied. Heavy work is unavailable until the cap is applied locally.</p> : null}
              {node.power.cap_required && node.power.cap_satisfied === null ? <p role="status">Required power cap status is unknown.</p> : null}
              {node.service_mode === 'attended' ? <p>Needs a signed-in desktop, for example to use Resolve.</p> : null}
              {node.service_mode === 'unattended' ? <p>Runs without a signed-in desktop.</p> : null}
              <h3>Watchdog</h3>
              {Object.keys(node.watchdog).length ? <ul>
                {Object.entries(node.watchdog).map(([label, status]) => <li key={label}>{safeRenderNodeLabel(label)}: {status}</li>)}
              </ul> : <p>Watchdog status is unknown.</p>}
              {node.state === 'quarantined' ? <p role="status">A runtime is quarantined. An owner can reset the watchdog after checking the node.</p> : null}
              {isOwner ? <div aria-label={`Owner controls for ${safeRenderNodeLabel(node.label)}`}>
                {(['mode', 'wake', 'sleep', 'restart', 'shutdown', 'reset'] as const).map((control) => {
                  const reason = unavailableReason(node, control, entitled, pending, fresh, workspaceId);
                  return <div key={control}>
                    <button type="button" disabled={Boolean(reason)} title={reason || undefined}
                      role={control === 'mode' ? 'switch' : undefined}
                      aria-checked={control === 'mode' ? node.processing_mode !== null && node.processing_mode !== 'off' : undefined}
                      onClick={() => void controlNode(node, control)}>
                      {control === 'mode' ? node.processing_mode === 'off' ? 'Enable processing mode' : 'Restore normal mode' : CONTROL_LABELS[control]}
                    </button>
                    {reason ? <p className="note">{reason}</p> : null}
                  </div>;
                })}
              </div> : null}
            </article>
          ))}
        </div>
      </> : null}
    </section>
  );
}
